import React, { useState, useRef, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    StatusBar,
    Platform,
    Image,
    Alert,
    TextInput,
    SafeAreaView,
} from 'react-native';
import { COLORS } from '../../theme/themeTokens';
import { COLORS_WRONG } from '../../theme/colorsWrong';
import PINDots from '../../components/UIComponents/PINDots';
import BackButton from '../../components/UIComponents/BackButton';
import PinKeypad, { PIN_INPUT_PROPS } from '../../../web/PinKeypad';

const PIN_LENGTH = 4;

interface PINSetupScreenProps {
    onComplete: (pin: string) => Promise<void>;
    onSkip: () => void;
}

type SetupStep = 'enter' | 'confirm';

const PINSetupScreen: React.FC<PINSetupScreenProps> = ({ onComplete, onSkip }) => {
    const statusBarHeight = Platform.OS === 'android' ? 24 : 47;

    const [step, setStep] = useState<SetupStep>('enter');
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const inputRef = useRef<TextInput>(null);
    const currentPin = step === 'enter' ? pin : confirmPin;

    useEffect(() => {
        const timer = setTimeout(() => {
            inputRef.current?.focus();
        }, 300);
        return () => clearTimeout(timer);
    }, [step]);

    const handleConfirmPin = async (confirmedPin: string) => {
        if (confirmedPin !== pin) {
            setError('PINs do not match. Please try again.');
            setConfirmPin('');
            setPin('');
            setStep('enter');
            return;
        }

        setIsLoading(true);
        try {
            await onComplete(pin);
        } catch (err) {
            setError('Failed to save PIN. Please try again.');
            setConfirmPin('');
            setPin('');
            setStep('enter');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePinChange = (value: string) => {
        const digits = value.replace(/[^0-9]/g, '').slice(0, PIN_LENGTH);
        setError(null);

        if (step === 'enter') {
            setPin(digits);
            if (digits.length === PIN_LENGTH) {
                setTimeout(() => setStep('confirm'), 200);
            }
        } else {
            setConfirmPin(digits);
            if (digits.length === PIN_LENGTH) {
                setTimeout(() => handleConfirmPin(digits), 200);
            }
        }
    };

    const handleSkip = () => {
        Alert.alert(
            'Skip PIN Setup?',
            'Without a PIN, your account will be less secure. Anyone with access to your device can open the app.',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Skip Anyway',
                    style: 'destructive',
                    onPress: onSkip,
                },
            ]
        );
    };

    const handleBack = () => {
        if (step === 'confirm') {
            setStep('enter');
            setConfirmPin('');
            setError(null);
        }
    };



    return (
        <View style={styles.container}>
            {/* <StatusBar barStyle="dark-content" backgroundColor={COLORS.pureLight} /> */}

            {/* Hidden system keyboard input */}
            <TextInput
                ref={inputRef}
                value={currentPin}
                onChangeText={handlePinChange}
                keyboardType="number-pad"
                maxLength={PIN_LENGTH}
                autoFocus
                editable={!isLoading}
                style={styles.hiddenInput}
                caretHidden
                {...PIN_INPUT_PROPS}
            />

            {/* Header */}
            <View style={[styles.headerBackground, { paddingTop: statusBarHeight }]}>
                {/* Blue glow effects */}
                {/* <Image
                    source={require('../../assets/blur-left.png')}
                    style={styles.blurLeft}
                />
                <Image
                    source={require('../../assets/blur-right.png')}
                    style={styles.blurRight}
                /> */}

                {/* Back Button (only in confirm step) */}
                {step === 'confirm' && (
                    <BackButton onPress={handleBack} />
                )}

                {/* Skip Button */}
                <TouchableOpacity style={styles.skipButton} onPress={handleSkip}>
                    <Text style={styles.skipText}>Skip</Text>
                </TouchableOpacity>
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {/* Icon */}
                <View style={styles.iconContainer}>
                    <Image source={require('../../assets/Secure-lock.png')} style={{ width: 80, height: 80 }} resizeMode="contain" />
                </View>

                {/* Title */}
                <Text style={styles.title}>
                    {step === 'enter' ? 'Your final step...' : 'Confirm PIN...'}
                </Text>
                <Text style={styles.subtitle}>
                    {step === 'enter'
                        ? 'Enter 4 numbers to keep your account safe'
                        : 'Re-enter your PIN to confirm'}
                </Text>

                {/* PIN Dots */}
                <PINDots
                    pin={currentPin}
                    pinLength={PIN_LENGTH}
                    error={error}
                    onPress={() => {
                        inputRef.current?.blur();
                        setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                />

                {/* Error Message */}
                {error && <Text style={styles.errorText}>{error}</Text>}

                <PinKeypad
                    value={currentPin}
                    length={PIN_LENGTH}
                    onChange={handlePinChange}
                    disabled={isLoading}
                    style={styles.keypad}
                />

                {Platform.OS === 'android' && (
                    <TouchableOpacity
                        style={styles.keyboardHint}
                        onPress={() => {
                            inputRef.current?.blur();
                            setTimeout(() => inputRef.current?.focus(), 50);
                        }}>
                        <Text style={styles.keyboardHintText}>Tap here to open keyboard</Text>
                    </TouchableOpacity>
                )}

            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.white,
    },
    headerBackground: {
        backgroundColor: COLORS.white,
        paddingBottom: 20,
        overflow: 'hidden',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingHorizontal: 20,
    },
    blurLeft: {
        position: 'absolute',
        width: 208,
        height: 208,
        left: -64,
        top: 40,
    },
    blurRight: {
        position: 'absolute',
        width: 208,
        height: 208,
        right: -64,
        top: 40,
    },

    skipButton: {
        marginLeft: 'auto',
    },
    skipText: {
        fontSize: 18,
        fontWeight: '500',
        color: COLORS.blueSteel,
    },
    mainContent: {
        flex: 1,
        backgroundColor: COLORS.white,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        paddingTop: 40,
        alignItems: 'center',
    },
    iconContainer: {
        marginBottom: 24,
        width: 92,
        height: 92,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'visible',
    },
    lockIcon: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: COLORS.softGrey,
        justifyContent: 'center',
        alignItems: 'center',
    },
    lockIconText: {
        fontSize: 36,
    },
    title: {
        fontFamily: 'Inter',
        fontSize: 26,
        fontWeight: 'bold',
        color: COLORS.brandOrange,
        marginBottom: 8,
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.deepNavy,
        textAlign: 'center',
        marginBottom: 32,
        paddingHorizontal: 40,
    },

    errorText: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS_WRONG.error,
        textAlign: 'center',
        marginBottom: 16,
    },
    keypad: {
        marginTop: 'auto',
        marginBottom: 32,
    },
    hiddenInput: {
        position: 'absolute',
        opacity: 0,
        width: 1,
        height: 1,
    },
    keyboardHint: {
        marginTop: 32,
        paddingVertical: 12,
        paddingHorizontal: 24,
    },
    keyboardHintText: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.blueSteel,
        textAlign: 'center',
    },
});

export default PINSetupScreen;

